<template>
  <section>
    <div>{{ conditionDisplayText(value) }}</div>
    <template v-if="value.subRules && value.subRules.length">
      <ul
        v-for="(cond,ix) in value.subRules"
        :key="ix"
        class="mb-0"
      >
        <li>{{ conditionDisplayText(cond) }}
          <AdvanceRequisiteRulesReadonly
            v-if="cond.subRules"
            :value="cond.subRules"
            class="mt-2"
          />
        </li>
      </ul>
    </template>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import useCourse from '@cm/client/composables/useCourse'

export default {
  name: 'AdvanceRequisiteConditionReadonly',
  components: {
    AdvanceRequisiteRulesReadonly: () => import('./AdvanceRequisiteRulesReadonly.vue'),
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    level: {
      type: Number,
      default: 0,
    },
  },
  setup () {
    const {
      generateCourseLabel,
    } = useCourse()
    return {
      generateCourseLabel,
    }
  },
  computed: {
    ...mapGetters('curriculumCourses', [ 'cmCourseByEffectiveDatesRangeLookup' ]),
    ...mapGetters('departments', [ 'departmentDisplayName' ]),
  },
  methods: {
    courseLabel (course) {
      let label
      if (course.courseId) { // Direct course Select
        const courseData = this.cmCourseByEffectiveDatesRangeLookup(course.courseId)
        if (!courseData) return `${course.subjectCode} ${course.courseNumber} test`
        label = this.generateCourseLabel(courseData)

      } else { // Dynamic Course Criteria
        const subjectCode = course.subjectCode
        if (course.type === 'simple') {
          label = `${subjectCode} ${course.courseNumber}`
        } else if (course.type === 'range') {
          label = `${subjectCode} courses with a course number range from ${course.min} to ${course.max}`
        } else if (course.type === 'wildcard') {
        // TODO add wildcard prefix with fixed suffix support
          if (course.courseNumber === '@') label = `any ${subjectCode} course`
          else label = `a ${subjectCode} course starting with number ${course.courseNumber.split('@')[0]}, `
        }
        if (course.withConditions && course.withConditions.length) {
          label += ` where ${course.withConditions.map(c => `${c.condition} ${c.comparator} ${c.value}`).join(', ')}`
        }
      }
      return label || 'Unknown Course'
    },
    grammarList (strings, conjunction) {
      if (strings.length === 0) return ''
      if (strings.length === 1) return strings[0]
      if (strings.length === 2) return `${strings[0]} ${conjunction} ${strings[1]}`

      // We use the Oxford comma
      const list = strings.slice(0, strings.length - 1).join(', ')
      return `${list}, ${conjunction} ${strings[strings.length - 1]}`
    },
    courseList (courses, conjunction) {
      const labels = courses.map(course => this.courseLabel(course))
      return this.grammarList(labels, conjunction)
    },
    doPlural (word, number) {
      let singular = word
      if (singular === 'classes') singular = 'class'
      if (singular === 'credits') singular = 'credit'
      if (number === 1) return singular

      // -es rules
      if (singular.charAt(singular.length - 1) === 's') return `${singular}es`
      return `${singular}s`
    },
    conditionDisplayText (value) {
      const { condition, type, min, max, courses = [], blocks = [], text, subRules } = value
      const orList = this.courseList(courses, 'or')
      const andList = this.courseList(courses, 'and')
      const blockList = this.grammarList(blocks, 'or')

      switch (condition) {
        case 'transfer':
        case 'passFail':
          const suffix = (condition === 'transfer') ? 'transferred' : 'take as pass/fail'
          if (max === 0) {
            if (!courses.length) return `No ${type} may be ${suffix}`
            return `${this.courseList(courses, 'AND')} may not be ${suffix}`
          } else {
            if (!courses.length) return `At most ${max} ${this.doPlural(type, max)} may be ${suffix}`
            return `At most ${max} ${this.doPlural(type, max)} from ${orList} may be ${suffix}`
          }

        // I don't think we ever set MAX for either of these
        case 'residential':
        case 'lastResidential':
          const last = condition === 'lastResidential' ? 'the last ' : ''
          return `At least ${last}${min} ${this.doPlural(type, min)} must be taken in residence`

        case 'perDiscipline':
          if (max === undefined) {
            return `At least ${min} ${this.doPlural(type, min)} must be taken in each department`
          } else {
            if (min === undefined) return `At most ${max} ${this.doPlural(type, max)} must be taken in each department`
            return `Between ${min} and ${max} ${type} may be taken in each department`
          }

        case 'perTerm':
          if (!courses.length) {
            return `Only ${max} ${this.doPlural(type, max)} may be taken each term`
          } else {
            return `Only ${max} ${this.doPlural(type, max)} from ${andList} may be taken each term`
          }

        case 'scribe':
          return `Completed Scribe block ${text}`

        case 'subRules':
          // TODO add either
          const actualRules = subRules.filter(rule => rule.condition !== 'freeformText').length
          if (max === undefined) {
            if (min === actualRules) return 'Completed all of the following:'
            if (min === 1) {
              if (actualRules === 2) return 'Completed either of the following:'
              return 'Completed any of the following:'
            }
            if (min >= 1) return `Completed ${min} of the following:`
            // Default to showing nothing
            return
          } else {
            if (min === undefined) return `Completed at most ${max} of the following`
            return `Completed between ${min} and ${max} of the following`
          }

        case 'spread':
          if (max === undefined) {
            return `Courses must be taken in at least ${min} different departments`
          } else {
            if (min === undefined) return `Courses may be taken in at most ${max} different departments`
            return `Courses must be taken in between ${min} and ${max} different departments`
          }

        case 'grade':
          if (!courses.length) {
            return `Only grades above ${min} will count for credit`
          } else {
            return `Only grades above ${min} will count for credit in ${andList}`
          }

        case 'gpa':
          if (!courses.length) {
            return `Must have a GPA of ${min} or above`
          } else {
            return `Must have a GPA of ${min} or above in ${andList}`
          }

        case 'share':
          return `Credits used to satisfy this requirement may also be used to satisfy ${blockList} requirements`

        case 'complete':
          return `Requirement is completed`

        case 'incomplete':
          return `Requirement is NOT completed`

        // Often it's better to use text rules than noncourse rules
        case 'noncourse':
          if (min === undefined || min === 1) return `Completed ${text}`
          return `Completed ${text} ${min} times`

        case 'course':

          // This rule is used a lot, so it's important we use the most natural
          // language possible (that's why the logic is so complex)
          if (!courses.length) {
            if (type === 'credits') {
              // Taken 2 credits from Math 21 or Math 25
              if (max === undefined) return `At least ${min} ${this.doPlural('credits', min)} must be taken`

              // Taken between 3 and 5 credits from Math 21 or Math 25
              return `Between ${min} and ${max} credits must be taken`
            }
            if (max !== undefined) {
              // Take no classes
              if (max === 0) return `Take no ${type}`
              // At most 4 classes will be counted
              if (min === undefined) return `At most ${max} ${this.doPlural(type, max)} will be counted`
              // Take exactly 8 classes
              if (min === max) return `Take exactly ${min} ${this.doPlural(type, max)}`
              // Take between 10 and 22 credits
              return `Take between ${min} and ${max} ${type}`
            }
          } else if (courses.length === 1) {
            const courseStr = this.courseLabel(courses[0])
            // Take Math 55
            if (min === 1) return `Take ${courseStr}`
            // Math 55 will not be counted
            if (max === 1) return `${courseStr} will not be counted`
            // Take Math 55 2 times (uncommon)
            if (min && type === 'classes') return `Take ${courseStr} ${min} ${this.doPlural('time', min)}`
            // Take Math 55 for 5 credits
            if (min && type === 'credits') return `Take ${courseStr} for ${min} credits`
          } else {
            if (type === 'classes') {
              if (max === undefined) {
                // Take Math 55 or CS 121
                if (min === 1) return `Take ${orList}`
                // Take Math 55 and CS 121
                if (min === courses.length) return `Take ${andList}`
                // Take 2 classes from Math 21, Math 25, or Math 55
                return `Take ${min} classes from ${orList}`
              } else {
                // Math 21 and Math 25 will not be counted
                if (max === 0) return `${andList} will not be counted`
                // Take at most 1 class from Math 21, Math 25, or Math 55
                if (min === undefined) return `Take at most ${max} ${this.doPlural('classes', max)} from ${orList}`
                // Take 0 classes from Math 21 or Math 25
                if (max === 0) return `Take ${max} ${this.doPlural('classes', max)} from ${orList}`
                // Take between 2 and 3 classes from Math 21, Math 25, or Math 55
                return `Take between ${min} and ${max} classes from ${orList}`
              }
            } else {
              // Take 2 credits from Math 21 or Math 25
              if (max === undefined) return `Take ${min} ${this.doPlural('credits', min)} from ${orList}`

              // Take 0 credits from Math 21 or Math 25
              if (max === 0) return `Take ${max} ${this.doPlural('classes', max)} from ${orList}`

              // Take between 3 and 5 credits from Math 21 or Math 25
              return `Take between ${min} and ${max} credits from ${orList}`
            }
          }
          return `Take ${andList}`

        case 'credential':
          const quantity = min || 1

          if (quantity === 1) {
            if (blockList.length === 1) return `Completed a ${blocks[0]}`
            return `Completed a ${blockList}`
          }
          return `Completed ${min} ${blockList} credentials`

        // These are bound to look kinda ugly, and only exist so we can have
        // a complete Scribe integration
        case 'if':
          return `If ${text}:`
        case 'elseIf':
          return `Otherwise, if ${text}:`
        case 'else':
          return `Otherwise:`

        case 'freeformText':
          return text

        default:
          return 'Unknown requirement condition'
      }
    },
  },
}
</script>
